import React, { useState, useEffect } from 'react';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';
import { UiDialog } from '../../../ui/ui-dialog';

import { ICON_NAMES, MaterialDesignIcon } from '../../../util/material-design-icons';
import { timeoutPromise } from '../../../util/timeout-promise';
import Message from '../../../widgets/legacy-message';
import { ReactComponent as LoadingLogo } from '../../../widgets/loading-logo.svg';
import { TableHeaderCell } from '../../../widgets/table-components/table-header-cell';

type Props = EmployeeDirectoryWizardStepProps;

export const EmployeeDirectoryImportGroups = ({
  employeeDirectoryImportId,
  onNext,
  onCancel,
  PageIndicator,
}: Props) => {
  const [groupResponse, setGroupResponse] = useState<{
    groupNameCounts: { [groupName: string]: number };
    currentGroups: Array<EmployeeGroup>;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!hasInitialized) {
      const getGroupData = () =>
        Api.get(`/employee-directory-import/${employeeDirectoryImportId}/groups`)
          .then((resp) => {
            resp
              .json()
              .then(
                (data: {
                  groupNameCounts: { [groupName: string]: number };
                  currentGroups: Array<EmployeeGroup>;
                  errorMessage?: string;
                }) => {
                  if (data.errorMessage || typeof data !== 'object') {
                    setErrorMessage('Failed to load data');
                  } else if (Object.keys(data.groupNameCounts).length === 0) {
                    onNext();
                  } else {
                    setGroupResponse(data);
                  }
                },
              );
          })
          .catch(() => {
            setErrorMessage('Failed to load data');
          });

      timeoutPromise(getGroupData, () =>
        setErrorMessage('Request to get employee groups timed out'),
      );

      setHasInitialized(true);
    }
  }, [hasInitialized, employeeDirectoryImportId, onNext]);

  const handleOnNext = async () => {
    setIsSaving(true);

    const postGroups = () =>
      Api.post(`/employee-directory-import/${employeeDirectoryImportId}/groups`)
        .then((resp: any) => {
          const result = resp?.json();
          setIsSaving(false);
          if (!result || result.errorMessage) {
            setErrorMessage('There was an error saving data');
          } else {
            onNext();
          }
        })
        .catch(() => setErrorMessage('There was an error saving data'));

    timeoutPromise(postGroups, () =>
      setErrorMessage('Request to create and update employee groups timed out'),
    );
  };

  const groups = Object.keys(groupResponse?.groupNameCounts || {});

  return (
    <>
      <UiDialog.Content>
        <PageIndicator />
        <span className="typo-h5">Groups</span>
        <p className="typo-subtitle">
          These are the groups that we found in the import data. We will create any new groups, and
          then associate the correct employees with the correct groups from this list.
        </p>
        {errorMessage ? <Message variant="error">{errorMessage}</Message> : null}
        {!groupResponse && !errorMessage ? (
          <div className="u-textAlign-center">
            <LoadingLogo />
          </div>
        ) : null}
        {groupResponse ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableHeaderCell>Group Name</TableHeaderCell>
                <TableHeaderCell># Imported Employees</TableHeaderCell>
                <TableHeaderCell>Will New Group Be Added?</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group}>
                  <TableCell>{group}</TableCell>
                  <TableCell>{groupResponse.groupNameCounts[group]}</TableCell>
                  <TableCell>
                    {!groupResponse.currentGroups.find((g) => g.name === group) ? (
                      <MaterialDesignIcon iconName={ICON_NAMES.CHECK_CIRCLE} />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        <UiButton
          variant="primary"
          disabled={Boolean(errorMessage)}
          onClick={handleOnNext}
          loading={isSaving}
        >
          Save
        </UiButton>
      </UiDialog.Actions>
    </>
  );
};
